import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FaEnvelope, FaInstagram, FaClock, FaTimes, FaCheckCircle, FaUsers, FaRegEye, FaBook } from 'react-icons/fa';
import '../styles/contact.css';

const asArray = (value) => (Array.isArray(value) ? value : []);
const corporatePointIcons = [FaUsers, FaRegEye, FaBook];

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');
  const { t } = useTranslation();
  const corporate = t('contactPage.corporate', { returnObjects: true });
  const corporatePoints = asArray(corporate.points);

  // Auto-close success modal after 5 seconds
  useEffect(() => {
    if (showSuccessModal) {
      const timer = setTimeout(() => {
        setShowSuccessModal(false);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [showSuccessModal]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (error) setError('');
  };

  const closeSuccessModal = () => {
    setShowSuccessModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form
    if (!formData.name || !formData.email || !formData.subject || !formData.message) {
      setError(t('contactPage.errors.required'));
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch('/api/send-contact-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });

      const result = await response.json();

      if (response.ok) {
        // Show success modal and reset form
        setShowSuccessModal(true);
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        console.error('❌ API returned error:', result);
        throw new Error(result.error || 'Failed to send message');
      }

    } catch (error) {
      console.error('❌ Error sending email:', error);
      setError(t('contactPage.errors.failed', { message: error.message }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-page">
      {/* Success Modal */}
      {showSuccessModal && (
        <div className="success-modal-overlay" onClick={closeSuccessModal}>
          <div className="success-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeSuccessModal}>
              <FaTimes />
            </button>
            <div className="modal-icon">
              <FaCheckCircle />
            </div>
            <h3>{t('contactPage.successTitle')}</h3>
            <p>
              {t('contactPage.successBody')}
            </p>
            <div className="modal-timer">
              {t('contactPage.successTimer')}
            </div>
          </div>
        </div>
      )}

      <section id="corporate" className="contact-corporate">
        <div className="container">
          <div className="contact-corporate-panel">
            <div className="contact-corporate-copy">
              <h1>{corporate.title}</h1>
              <p>{corporate.subtitle}</p>
            </div>

            <div className="contact-corporate-points">
              {corporatePoints.map((point, index) => {
                const PointIcon = corporatePointIcons[index] || FaCheckCircle;

                return (
                  <div key={point} className="contact-corporate-point">
                    <span aria-hidden="true">
                      <PointIcon />
                    </span>
                    <strong>{point}</strong>
                  </div>
                );
              })}
            </div>

            <p className="contact-corporate-helper">{corporate.helper}</p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Information */}
            <div className="contact-info">
              <h2>{t('contactPage.contactInfoTitle')}</h2>
              <p className="contact-description">
                {t('contactPage.contactInfoDescription')}
              </p>

              <div className="contact-methods">
                <div className="contact-method">
                  <div className="contact-icon">
                    <FaEnvelope />
                  </div>
                  <div className="contact-details">
                    <h3>{t('contactPage.email')}</h3>
                    <a href="mailto:contact@massal.ai">contact@massal.ai</a>
                    <p>{t('contactPage.emailResponse')}</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-icon">
                    <FaInstagram />
                  </div>
                  <div className="contact-details">
                    <h3>{t('contactPage.instagram')}</h3>
                    <a href="https://instagram.com/massal.ai" target="_blank" rel="noopener noreferrer">
                      @massal.ai
                    </a>
                    <p>{t('contactPage.instagramFollow')}</p>
                  </div>
                </div>

                <div className="contact-method">
                  <div className="contact-icon">
                    <FaClock />
                  </div>
                  <div className="contact-details">
                    <h3>{t('contactPage.responseTime')}</h3>
                    <p>{t('contactPage.hoursWeekdays')}</p>
                    <p>{t('contactPage.responseGoal')}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="contact-form-container">
              <h2>{t('contactPage.formTitle')}</h2>
              <form className="contact-form" onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="name">{t('contactPage.fields.name')}</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.fields.namePlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email">{t('contactPage.fields.email')}</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.fields.emailPlaceholder')}
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="subject">{t('contactPage.fields.subject')}</label>
                  <select
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">{t('contactPage.subjectOptions.placeholder')}</option>
                    <option value="general">{t('contactPage.subjectOptions.general')}</option>
                    <option value="order">{t('contactPage.subjectOptions.order')}</option>
                    <option value="custom">{t('contactPage.subjectOptions.custom')}</option>
                    <option value="corporate">{t('contactPage.subjectOptions.corporate')}</option>
                    <option value="technical">{t('contactPage.subjectOptions.technical')}</option>
                    <option value="feedback">{t('contactPage.subjectOptions.feedback')}</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="message">{t('contactPage.fields.message')}</label>
                  <textarea
                    id="message"
                    name="message"
                    rows="5"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    placeholder={t('contactPage.fields.messagePlaceholder')}
                  ></textarea>
                </div>

                {error && (
                  <div className="error-message">
                    {error}
                  </div>
                )}

                <button
                  type="submit"
                  className="submit-button"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.buttons.sending') : t('common.buttons.sendMessage')}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Quick Links */}
      <section className="contact-faq">
        <div className="container">
          <h2>{t('contactPage.faqTitle')}</h2>
          <div className="faq-grid">
            {t('contactPage.faq', { returnObjects: true }).map((faq, index) => (
              <div key={index} className="faq-card">
                <h3>{faq.question}</h3>
                <p>{faq.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
